import { Box, Button, Card, Form, FormButtonRow, IconButton, Spinner, Text } from '@ksyos/design-system';
import { chunk } from 'lodash';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization, OrganizationType, Profession, ValidationErrorCode } from '@common/enums';
import { imageExtensions } from '@common/utils/fileExtensions';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import type { IEmployment } from '../../interfaces/employment';
import type { IOrganization } from '../../interfaces/organization';
import { DangerAlertWhenErrorMessage } from '../../elements/common';
import { ConfirmationModal } from '../../elements/modals/ConfirmationModal';
import { ImageThumbnail } from '../../userDataManagement/ImageThumbnail';

interface IOrganizationStampProps {
    employmentId: string;
    isReadOnly: boolean;
    organizationId?: string;
}

export function OrganizationStamp(props: IOrganizationStampProps) {
    const { employmentId, isReadOnly, organizationId } = props;
    const fileInput = useRef(null);
    const organizationStampFormRef = useRef<HTMLFormElement>(null);
    const maxFileSizeInMegaBytes = 10;
    const abortableFetch = useAbortableFetch();
    const authenticatedUserSelectedEmploymentId = useSelector(
        (state: IReduxState) => state.authentication.selectedEmploymentId
    );
    const authenticatedUserIsEmploymentSettingsEditOrReadManager = hasOneAuthorization([
        ManagerAuthorization.employmentSettingsEdit,
        ManagerAuthorization.employmentSettingsRead,
    ]);
    const authenticatedUserIsPractitionerOrManager =
        authenticatedUserSelectedEmploymentId === employmentId ||
        authenticatedUserIsEmploymentSettingsEditOrReadManager;

    const [activeEmployment, setActiveEmployment] = useState<IEmployment>();
    const [isGeneralPractitionerOrHidhaOrWaarnemer, setIsGeneralPractitionerOrHidhaOrWaarnemer] = useState(false);
    const [isOrganizationGpPractice, setIsOrganizationGpPractice] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [newFile, setNewFile] = useState<File | undefined>(undefined);
    const [organizationStampThumb, setOrganizationStampThumb] = useState<string>();
    const [error, setError] = useState<string>();
    const [gpColleagueEmployments, setGpColleagueEmployments] = useState<IEmployment[]>();
    const [showUploadForColleaguesModal, setShowUploadForColleaguesModal] = useState(false);

    useLayoutEffect(() => {
        if (window.location.hash === '#organizationStamp' && organizationStampFormRef.current !== null) {
            organizationStampFormRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            });
        }
    });

    useEffect(() => {
        if (activeEmployment && authenticatedUserIsEmploymentSettingsEditOrReadManager) {
            loadGpColleagueEmployments();
        }
    }, [activeEmployment?.id]);

    useEffect(() => {
        initialize();
    }, [employmentId, organizationId]);

    if (!activeEmployment || (!isGeneralPractitionerOrHidhaOrWaarnemer && !isOrganizationGpPractice)) {
        return null;
    }

    return (
        <div>
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        !isReadOnly && !isEditing && activeEmployment.organizationStampUrl ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translateKey('personalSettings.organizationStamp.formTitle')}
                />
                <Card.Body>
                    <Form
                        formRef={organizationStampFormRef}
                        onSubmit={async (e) => {
                            e.preventDefault();
                            await handleSubmitSingleEmployment();
                        }}
                        submitOnEnter={false}
                    >
                        <DangerAlertWhenErrorMessage errorMessage={error} />

                        {!organizationStampThumb && (
                            <>
                                {!isEditing && (
                                    <Text mb={6}>
                                        {translateKey('personalSettings.organizationStamp.addStampExplanation')}
                                    </Text>
                                )}
                                <Button
                                    iconLeft="add"
                                    isDisabled={!authenticatedUserIsPractitionerOrManager}
                                    onClick={openFileSelect}
                                    variant="link"
                                >
                                    {translateKey('personalSettings.organizationStamp.addStamp')}
                                </Button>
                                <input
                                    accept={imageExtensions}
                                    className="hidden"
                                    onChange={async (e) => await handleFileSelected(e.target.files)}
                                    ref={fileInput}
                                    type="file"
                                />
                            </>
                        )}

                        {organizationStampThumb && (
                            <ImageThumbnail
                                onClick={!isEditing ? () => window.open(organizationStampThumb, '_blank') : undefined}
                                onRemove={isEditing ? removeOrganizationStamp : undefined}
                                src={organizationStampThumb}
                            />
                        )}

                        {isEditing && (
                            <FormButtonRow>
                                {!isSaving && (
                                    <Button
                                        onClick={handleCancel}
                                        type="button"
                                        variant="outline"
                                    >
                                        {translateKey('common.cancel')}
                                    </Button>
                                )}
                                <Button
                                    isDisabled={isSaving}
                                    onClick={handleSubmitSingleEmployment}
                                    type="button"
                                    variant="solid"
                                >
                                    <Box
                                        alignItems="center"
                                        display="flex"
                                    >
                                        {isSaving && <Spinner style={{ marginRight: '0.5rem' }} />}
                                        {translateKey('personalSettings.organizationStamp.save')}
                                    </Box>
                                </Button>
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>

            <ConfirmationModal
                cancelText={translateKey('common.no')}
                confirmText={translateKey('personalSettings.organizationStamp.modalYes')}
                disabled={isSaving}
                handleNo={async () => setShowUploadForColleaguesModal(false)}
                handleYes={async () => handleSubmitColleagues()}
                isLoading={isSaving}
                isOpen={showUploadForColleaguesModal}
                question={translateKey('personalSettings.organizationStamp.modalText')}
                title={translateKey('personalSettings.organizationStamp.modalHeader')}
            />
        </div>
    );

    function openFileSelect() {
        (fileInput as any).current.click();
    }

    function removeOrganizationStamp() {
        setNewFile(undefined);
        setOrganizationStampThumb(undefined);
    }

    async function handleFileSelected(files?: FileList | null) {
        if (files && files.length > 0) {
            const file = files[0];

            if (file.size > maxFileSizeInMegaBytes * 1024 * 1024) {
                setError(
                    translateKey(
                        `errorCodes.${ValidationErrorCode.IMAGE_MAX_FILESIZE_EXCEEDED}`,
                        `${maxFileSizeInMegaBytes} MB`
                    )
                );
            } else {
                setError(undefined);
                setNewFile(file);

                const fileReader = new FileReader();
                fileReader.onload = () => {
                    setOrganizationStampThumb(fileReader.result as string);
                    setIsEditing(true);
                };
                fileReader.readAsDataURL(file);
            }
        }
    }

    async function initialize() {
        await loadEmployment();
        await loadOrganization();
    }

    async function loadEmployment() {
        const url = `/api/employments/${employmentId}?includeOrganizationStamp=true`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const employment: IEmployment = result.json;

        if (isActiveOnDate(employment, new Date())) {
            setActiveEmployment(employment);

            if (
                employment.profession &&
                [
                    Profession.generalPractitioner,
                    Profession.generalPractitionerHidha,
                    Profession.generalPractitionerMentalHealthSpecialist,
                    Profession.generalPractitionerWaarnemer,
                ].includes(employment.profession)
            ) {
                setIsGeneralPractitionerOrHidhaOrWaarnemer(true);
            }
        }
        if (employment.organizationStampUrl) {
            setOrganizationStampThumb(employment.organizationStampUrl);
        }
    }

    async function loadOrganization() {
        if (organizationId) {
            const url = `/api/organizations/${organizationId}`;
            const result = await abortableFetch.fetchJson(url, 'GET');
            const organization: IOrganization = result.json;
            if (organization.organizationType === OrganizationType.gpPractice) {
                setIsOrganizationGpPractice(true);
            }
        }
    }

    async function loadGpColleagueEmployments() {
        let url = `/api/employments/${organizationId}/practitioners?includeOrganizationStamp=true`;
        if (isGeneralPractitionerOrHidhaOrWaarnemer) {
            url += `&professions=${Profession.generalPractitioner},${Profession.generalPractitionerHidha},${Profession.generalPractitionerMentalHealthSpecialist},${Profession.generalPractitionerWaarnemer}`;
        }
        const employmentsResult = await abortableFetch.fetchJson(url, 'GET');
        const colleagueEmployments = (employmentsResult.json.items as IEmployment[]).filter(
            (item) => item.id !== employmentId
        );
        setGpColleagueEmployments(colleagueEmployments);
    }

    function handleCancel() {
        setNewFile(undefined);
        setOrganizationStampThumb(activeEmployment!.organizationStampUrl);
        setIsEditing(false);
    }

    async function handleSubmitSingleEmployment() {
        if (newFile) {
            await uploadStampForEmployments([employmentId], newFile);
        } else if (activeEmployment!.organizationStampUrl && !organizationStampThumb) {
            await abortableFetch.fetchJson(`/api/employments/${employmentId}/organizationStamp`, 'DELETE');
        }
        setIsEditing(false);
        await initialize();
    }

    async function handleSubmitColleagues() {
        if (newFile) {
            const employmentIds = gpColleagueEmployments?.map((o) => o.id) ?? [];
            await uploadStampForEmployments(employmentIds, newFile);
        }
        setIsEditing(false);
        await initialize();
    }

    /**
     * Uploads a stamp for multiple employments in batches to avoid overwhelming the backend.
     *
     * @param {string[]} employmentIds - Array of employment IDs to upload the stamp for.
     * @param {File} stamp - The stamp file to be uploaded.
     */
    async function uploadStampForEmployments(employmentIds: string[], stamp: File) {
        if (!employmentIds.length) {
            return;
        }

        setIsSaving(true);

        const batchSize = 3;
        const employmentIdChunks = chunk(employmentIds, batchSize);

        for (const employmentIdChunk of employmentIdChunks) {
            try {
                await Promise.all(
                    employmentIdChunk.map((employmentIdString: string) => uploadStamp(employmentIdString, stamp))
                );
            } catch {
                setError(translateKey('validation.general'));
                break;
            }
        }

        setShowUploadForColleaguesModal(shouldShowUploadForColleaguesModal());
        setIsSaving(false);
    }

    async function uploadStamp(employmentIdStr: string, stamp: File) {
        await abortableFetch.uploadFile(`/api/employments/${employmentIdStr}/organizationStamp`, stamp);
    }

    function shouldShowUploadForColleaguesModal() {
        return (
            !showUploadForColleaguesModal &&
            Boolean(gpColleagueEmployments?.some((gpEmployment) => !gpEmployment.organizationStampUrl))
        );
    }
}
